import { create } from 'zustand'
import type { User as FirebaseUser } from 'firebase/auth'
import type { UserProfile, SubscriptionTier } from '@/types/user'

interface AuthState {
  user: FirebaseUser | null
  profile: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean

  // Actions
  setUser: (user: FirebaseUser | null) => void
  setProfile: (profile: UserProfile | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    }),

  setProfile: (profile) => set({ profile }),

  setLoading: (isLoading) => set({ isLoading }),

  logout: () =>
    set({
      user: null,
      profile: null,
      isAuthenticated: false,
    }),
}))

// Admin email check (fallback for users without tier set)
const ADMIN_EMAILS = ['admin@isqroll.co.nz', 'joel@temperocreative.co.nz', 'demo@isqroll.co.nz']

export const isAdmin = (email: string | null | undefined, profile?: UserProfile | null): boolean => {
  if (profile?.subscriptionTier === 'admin') return true
  return !!email && ADMIN_EMAILS.includes(email)
}

export function isDealer(profile: UserProfile | null): boolean {
  return profile?.subscriptionTier === 'dealership'
}

/** Check if user has one of the given subscription tiers */
export function hasTier(profile: UserProfile | null, ...tiers: SubscriptionTier[]): boolean {
  if (!profile) return false
  const userTier = profile.subscriptionTier || 'general'
  return tiers.includes(userTier)
}

/** Returns true for any non-general account tier (storefront, dealership, nonprofit, admin) */
export function isStoreAccount(profile: UserProfile | null): boolean {
  if (!profile) return false
  const tier = profile.subscriptionTier || 'general'
  return tier !== 'general'
}

/** Check if user's tier can create listings in a category group.
 *  Fundraiser group requires 'nonprofit' tier.
 *  Nonprofit tier can ONLY create in fundraiser group.
 *  Admins bypass all restrictions. */
export function canCreateInGroup(
  profile: UserProfile | null,
  email: string | null | undefined,
  groupId: string
): boolean {
  if (isAdmin(email, profile)) return true
  if (!profile) return false
  const tier = profile.subscriptionTier || 'general'
  // Nonprofits can ONLY create in fundraiser
  if (tier === 'nonprofit') return groupId === 'fundraiser'
  // Fundraiser requires nonprofit
  if (groupId === 'fundraiser') return false
  return true
}

/** Display label for a subscription tier */
export function getTierLabel(tier: SubscriptionTier | undefined): string {
  const labels: Record<SubscriptionTier, string> = {
    general: 'General',
    storefront: 'Storefront',
    dealership: 'Dealership',
    nonprofit: 'Nonprofit',
    admin: 'Admin',
  }
  return labels[tier || 'general']
}

/** Tailwind badge colour classes for a tier */
export function getTierColor(tier: SubscriptionTier | undefined): string {
  const colors: Record<SubscriptionTier, string> = {
    general: 'bg-slate-100 text-slate-700',
    storefront: 'bg-blue-100 text-blue-700',
    dealership: 'bg-purple-100 text-purple-700',
    nonprofit: 'bg-green-100 text-green-700',
    admin: 'bg-red-100 text-red-700',
  }
  return colors[tier || 'general']
}
